import React, { useRef, useEffect, useState } from 'react';
import { Phone, Mail, QrCode, Globe } from 'lucide-react';
import { SocialIcon } from '@/components/types/SocialIcon';

/* =============================================================================
   TIPOS
============================================================================= */

type Empresa = {
  empresa: string;
  logo: string | null;
  descripcion?: string | null;
  descripcion_corta?: string | null;
  telefono?: string | null;
  email?: string | null;
  direccion?: string | null;
};

type Persona = {
  nombre?: string | null;
  puesto?: string | null;
  email?: string | null;
  telefono?: string | null;
  direccion?: string | null;
  descripcion?: string | null;
  redes?: {
    id_tipo_red: string;
    nombre: string;
    icono?: string;
    url: string;
  }[];
};

export type InvitationDesignProps = {
  empresa: Empresa;
  persona?: Persona;
  qrCitasUrl?: string;
  qrCatalogoUrl?: string;
  reservasOn: boolean;
  palette: ZigZagPalette;
  /**
   * Cuando se proporciona, sobreescribe el aspect-ratio de CardShell
   * para capturar la tarjeta a unas dimensiones exactas (px).
   * No afecta al renderizado visual normal en pantalla.
   */
  cardShellStyle?: React.CSSProperties;
};

export type ZigZagPalette = {
  id: string;
  label: string;
  primary: string;
  secondary: string;
  accent: string;
  dark: string;
};

export const zigzagPalettes: ZigZagPalette[] = [
  {
    id: 'midnight',
    label: 'Noche',
    primary: '#0f172a',
    secondary: '#1e293b',
    accent: '#38bdf8',
    dark: '#0f172a',
  },
  {
    id: 'forest',
    label: 'Bosque',
    primary: '#14532d',
    secondary: '#166534',
    accent: '#86efac',
    dark: '#052e16',
  },
  {
    id: 'wine',
    label: 'Vino',
    primary: '#881337',
    secondary: '#9f1239',
    accent: '#fda4af',
    dark: '#4c0519',
  },
  {
    id: 'ocean',
    label: 'Océano',
    primary: '#1e3a5f',
    secondary: '#1d4ed8',
    accent: '#7dd3fc',
    dark: '#0c1a2e',
  },
  {
    id: 'dusk',
    label: 'Atardecer',
    primary: '#7c3aed',
    secondary: '#6d28d9',
    accent: '#fbbf24',
    dark: '#2e1065',
  },
  {
    id: 'slate',
    label: 'Pizarra',
    primary: '#334155',
    secondary: '#475569',
    accent: '#f1f5f9',
    dark: '#0f172a',
  },
];

/* =============================================================================
   CONSTANTES DE DISEÑO — exportadas para cálculos de captura
============================================================================= */

export const CARD_W = 360;
export const CARD_H = 200;
const CARD_GAP = 24;

/* =============================================================================
   HELPERS
============================================================================= */

const pickQR = ({
  reservasOn,
  qrCitasUrl,
  qrCatalogoUrl,
}: InvitationDesignProps) =>
  reservasOn && qrCitasUrl ? qrCitasUrl : qrCatalogoUrl;

const LogoOrInitial = ({
  logo,
  name,
  size = 48,
  borderColor,
  bgColor,
  textColor,
}: {
  logo: string | null;
  name: string;
  size?: number;
  borderColor?: string;
  bgColor?: string;
  textColor?: string;
}) => {
  const initial = (name ?? '').trim().charAt(0).toUpperCase() || '?';
  return logo ? (
    <img
      src={logo}
      alt={name}
      crossOrigin="anonymous"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        border: `2px solid ${borderColor ?? 'rgba(255,255,255,0.3)'}`,
        display: 'block',
      }}
    />
  ) : (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bgColor ?? 'rgba(255,255,255,0.15)',
        border: `2px solid ${borderColor ?? 'rgba(255,255,255,0.3)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 700,
        color: textColor ?? '#fff',
        fontFamily: 'Georgia, serif',
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
};

const CardShell = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      width: '100%',
      aspectRatio: `${CARD_W} / ${CARD_H}`,
      borderRadius: 16,
      overflow: 'hidden',
      position: 'relative',
      boxShadow:
        '0 4px 6px -1px rgba(0,0,0,0.1), 0 20px 40px -8px rgba(0,0,0,0.2)',
      flexShrink: 0,
      // style se aplica al final para que pueda sobrescribir aspectRatio
      // cuando se necesita captura a dimensiones exactas
      ...style,
    }}
  >
    {children}
  </div>
);

const DiagonalLines = ({
  color,
  opacity = 0.12,
}: {
  color: string;
  opacity?: number;
}) => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern
        id={`diag-${color.replace('#', '')}`}
        patternUnits="userSpaceOnUse"
        width="12"
        height="12"
        patternTransform="rotate(45)"
      >
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="12"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity={opacity}
        />
      </pattern>
    </defs>
    <rect
      width="100%"
      height="100%"
      fill={`url(#diag-${color.replace('#', '')})`}
    />
  </svg>
);

const DotsPattern = ({
  color,
  opacity = 0.15,
}: {
  color: string;
  opacity?: number;
}) => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern
        id={`dots-${color.replace('#', '')}`}
        patternUnits="userSpaceOnUse"
        width="16"
        height="16"
      >
        <circle cx="4" cy="4" r="1.5" fill={color} fillOpacity={opacity} />
      </pattern>
    </defs>
    <rect
      width="100%"
      height="100%"
      fill={`url(#dots-${color.replace('#', '')})`}
    />
  </svg>
);

const Row = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 7,
      fontSize: 11,
      color: 'rgba(255,255,255,0.85)',
    }}
  >
    <span
      style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0, marginTop: 1 }}
    >
      {icon}
    </span>
    {children}
  </div>
);

/* =============================================================================
   FRONT CARD — exportado para captura off-screen
============================================================================= */

export const FrontCard = ({
  empresa,
  persona,
  palette,
  cardShellStyle,
}: InvitationDesignProps) => {
  const descripcion =
    persona?.descripcion || empresa.descripcion_corta || empresa.descripcion;

  return (
    <CardShell style={cardShellStyle}>
      <div style={{ position: 'absolute', inset: 0, background: '#fff' }} />

      {/* Banda izquierda */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '33.33%',
          height: '100%',
          background: palette.primary,
        }}
      >
        <DiagonalLines color={palette.accent} opacity={0.18} />
        <div
          style={{
            position: 'absolute',
            right: '-11%',
            top: '-20%',
            width: '33%',
            height: '60%',
            borderRadius: '50%',
            background: palette.secondary,
            opacity: 0.4,
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: '-5%',
            bottom: '-15%',
            width: '22%',
            height: '40%',
            borderRadius: '50%',
            background: palette.accent,
            opacity: 0.15,
          }}
        />
      </div>

      {/* Logo empresa */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '33.33%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
        }}
      >
        <LogoOrInitial
          logo={empresa.logo}
          name={empresa.empresa}
          size={60}
          borderColor="rgba(255,255,255,0.4)"
          bgColor="rgba(255,255,255,0.15)"
          textColor="#fff"
        />
      </div>

      {/* Separador */}
      <div
        style={{
          position: 'absolute',
          left: '33.33%',
          top: '12%',
          width: 3,
          height: '76%',
          background: palette.accent,
          zIndex: 2,
          borderRadius: 2,
        }}
      />

      {/* Contenido derecho */}
      <div
        style={{
          position: 'absolute',
          left: 'calc(33.33% + 18px)',
          right: 20,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 5,
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontSize: persona?.nombre ? 15 : 19,
            fontWeight: 800,
            color: palette.dark,
            letterSpacing: '-0.4px',
            lineHeight: 1.2,
            fontFamily: 'Georgia,"Times New Roman",serif',
          }}
        >
          {empresa.empresa}
        </div>

        {persona?.nombre && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#475569',
                letterSpacing: '0.01em',
                lineHeight: 1.2,
                fontFamily: 'Georgia,"Times New Roman",serif',
              }}
            >
              {persona.nombre}
            </div>
            {persona.puesto && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: palette.primary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {persona.puesto}
              </div>
            )}
          </>
        )}

        <div
          style={{
            width: 36,
            height: 2,
            background: palette.primary,
            borderRadius: 1,
            opacity: 0.45,
          }}
        />

        {descripcion && (
          <div
            style={{
              fontSize: 10.5,
              color: '#64748b',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {descripcion}
          </div>
        )}
      </div>
    </CardShell>
  );
};

/* =============================================================================
   BACK CARD — exportado para captura off-screen
============================================================================= */

export const BackCard = (props: InvitationDesignProps) => {
  const { empresa, persona, palette, reservasOn, cardShellStyle } = props;
  const qr = pickQR(props);
  const redes = persona?.redes ?? [];
  const telefono = persona?.telefono || empresa.telefono;
  const email = persona?.email || empresa.email;

  return (
    <CardShell style={cardShellStyle}>
      <div
        style={{ position: 'absolute', inset: 0, background: palette.primary }}
      >
        <DotsPattern color={palette.accent} opacity={0.18} />
      </div>

      <div
        style={{
          position: 'absolute',
          right: '-8%',
          top: '-15%',
          width: '39%',
          height: '70%',
          borderRadius: '50%',
          background: palette.secondary,
          opacity: 0.5,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '-5%',
          bottom: '-15%',
          width: '28%',
          height: '50%',
          borderRadius: '50%',
          background: palette.accent,
          opacity: 0.1,
        }}
      />

      {/* Logo + nombre empresa */}
      <div
        style={{
          position: 'absolute',
          top: 18,
          left: 18,
          right: qr ? '36%' : 18,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          zIndex: 2,
        }}
      >
        <LogoOrInitial
          logo={empresa.logo}
          name={empresa.empresa}
          size={36}
          borderColor="rgba(255,255,255,0.35)"
          bgColor="rgba(255,255,255,0.12)"
          textColor="#fff"
        />
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.3px',
              lineHeight: 1.2,
              fontFamily: 'Georgia,serif',
            }}
          >
            {empresa.empresa}
          </div>
          <div
            style={{
              width: 24,
              height: 2,
              background: palette.accent,
              borderRadius: 1,
              marginTop: 4,
            }}
          />
        </div>
      </div>

      {/* Teléfono + email + redes */}
      <div
        style={{
          position: 'absolute',
          left: 18,
          top: 80,
          right: qr ? '36%' : 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          zIndex: 2,
        }}
      >
        {telefono && (
          <Row icon={<Phone style={{ width: 11, height: 11 }} />}>
            {telefono}
          </Row>
        )}
        {email && (
          <Row icon={<Mail style={{ width: 11, height: 11 }} />}>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {email}
            </span>
          </Row>
        )}
        {redes.slice(0, 2).map((r) => (
          <div
            key={r.id_tipo_red}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 11,
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            <SocialIcon
              network={r.nombre}
              size={11}
              color="rgba(255,255,255,0.5)"
              style={{ flexShrink: 0 }}
            />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {r.url}
            </span>
          </div>
        ))}
      </div>

      {/* QR */}
      <div
        style={{
          position: 'absolute',
          right: 14,
          bottom: 12,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
        }}
      >
        {qr ? (
          <>
            <div
              style={{
                background: '#fff',
                borderRadius: 10,
                padding: 6,
                boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
              }}
            >
              <img
                src={qr}
                alt="QR"
                crossOrigin="anonymous"
                style={{ width: 72, height: 72, display: 'block' }}
              />
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 20,
                padding: '3px 8px',
              }}
            >
              <QrCode style={{ width: 9, height: 9, color: palette.accent }} />
              <span
                style={{
                  fontSize: 8,
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                {reservasOn
                  ? 'Escanea para reservar'
                  : 'Escanea para ver la web'}
              </span>
            </div>
          </>
        ) : (
          <div
            style={{
              width: 92,
              height: 92,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.08)',
              border: '1.5px dashed rgba(255,255,255,0.25)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            <Globe
              style={{ width: 20, height: 20, color: 'rgba(255,255,255,0.3)' }}
            />
            <span
              style={{
                fontSize: 9,
                color: 'rgba(255,255,255,0.3)',
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              Sin QR
            </span>
          </div>
        )}
      </div>
    </CardShell>
  );
};

/* =============================================================================
   WRAPPER RESPONSIVO
============================================================================= */

type ScaledCardsProps = InvitationDesignProps & {
  forCapture?: boolean;
};

export const DesignZigZagHex = ({ forCapture, ...props }: ScaledCardsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isRow, setIsRow] = useState(false);

  useEffect(() => {
    if (forCapture) return;

    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      setIsRow(el.clientWidth >= CARD_W + CARD_GAP + 200);
    };

    update();
    const ro = new ResizeObserver(update);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [forCapture]);

  return (
    <div ref={wrapperRef} style={{ width: '100%', minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: isRow ? 'row' : 'column',
          alignItems: 'stretch',
          gap: CARD_GAP,
          width: '100%',
        }}
      >
        <div style={{ flex: isRow ? 1 : undefined, minWidth: 0 }}>
          <FrontCard {...props} />
        </div>
        <div style={{ flex: isRow ? 1 : undefined, minWidth: 0 }}>
          <BackCard {...props} />
        </div>
      </div>
    </div>
  );
};

/* =============================================================================
   COLOR PICKER
============================================================================= */

export const ZigZagColorPicker = ({
  value,
  onChange,
}: {
  value: ZigZagPalette;
  onChange: (p: ZigZagPalette) => void;
}) => (
  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
    {zigzagPalettes.map((p) => (
      <button
        key={p.id}
        onClick={() => onChange(p)}
        title={p.label}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: p.primary,
          border:
            value.id === p.id
              ? `3px solid ${p.accent}`
              : '3px solid transparent',
          outline: value.id === p.id ? `2px solid ${p.primary}` : 'none',
          outlineOffset: 2,
          cursor: 'pointer',
          transition: 'transform 0.15s',
          transform: value.id === p.id ? 'scale(1.2)' : 'scale(1)',
        }}
      />
    ))}
  </div>
);
